import { CancelTokenSource } from 'axios';
import { LinkItem } from './links';
import { ONE_MB } from '@app/constants/file';
import { OrderBy } from './common';
import { ReactNode } from 'react';
import { TabItem } from './tab';
import { z } from 'zod';

/**
 * Form
 */

export const insuranceDocumentFormSchema = z.object({
  dateRange: z
    .object({
      startDate: z.date({ required_error: 'Invalid date' }),
      endDate: z.date({ required_error: 'Invalid date' }),
    })
    .superRefine((data, ctx) => {
      if (data.endDate < data.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: 'End date cannot be less than start date',
        });
      }
    }),
  coverageAmount: z
    .string({ required_error: 'Invalid coverage amount' })
    .min(1, 'Invalid coverage amount')
    .refine((val) => !isNaN(Number(val)), 'Invalid coverage amount')
    .transform((val) => Number(val))
    .refine((val) => val > 0, 'Invalid coverage amount'),
  file: z
    .instanceof(File, { message: 'No file selected. Please choose a PDF, JPEG, or PNG file.' })
    .refine(
      (file) => ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type),
      { message: 'Unsupported file type. Upload a PDF, JPEG or PNG' },
    )
    .refine((file) => file.size <= 25 * ONE_MB, {
      message: 'File too large. Maximum file size is 25MB',
    }),
});

export type InsuranceDocumentFormModel = z.infer<typeof insuranceDocumentFormSchema>;

/**
 * General
 */

export enum UploadStatus {
  INIT = 'INIT',
  STARTED = 'STARTED',
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  FAILED = 'FAILED',
}

export type UploadItem = {
  id: string;
  file: File;
  progress: number;
  cancelTokenSource: CancelTokenSource;
  status: UploadStatus;
  helperMessage?: ReactNode;
  showErrorIcon?: boolean;
  documentTypeName?: string;
};

export interface UploadProgressEvent {
  loaded: number;
  total?: number;
}

export type UploadActions = {
  add: (files: File | FileList | File[]) => void;
  update: (id: string, file: File) => void;
  updateProgress: (id: string, progress: number, status?: UploadStatus) => void;
  cancel: (id: string) => void;
  cancelAll: () => void;
};

export type UploadManager = {
  isMultiFile: boolean;
  item: UploadItem | null;
  items: UploadItem[];
  hasFiles: boolean;
  actions: UploadActions;
};

export enum DocumentTypeName {
  GENERAL_LIABILITY = 'General Liability',
  RENTAL_FLOATER = 'Rental Floater',
  AUTO_LIABILITY = 'Auto Liability',
}

export enum DocumentTypeId {
  RENTAL_FLOATER = 1,
  GENERAL_LIABILITY = 3,
  AUTO_LIABILITY = 4,
  DRIVERS_LICENSE = 7,
}

export enum DocumentReviewStatus {
  DRAFT = 1,
  ABANDONED = 2,
  SUBMITTED = 3,
  APPROVED = 4,
  NOT_APPROVED = 5,
  EXPIRED = 6,
  MALWARE_DETECTED = 7,
}

export type DocumentView = TabItem & LinkItem;

/**
 * API
 */

export type GetDocumentsConfig = {
  type?: DocumentTypeId;
  page?: number;
  size?: number;
  sortBy?: string;
  orderBy?: OrderBy;
  statusIds?: DocumentReviewStatus[];
};

export type Document = {
  company_document_id: number;
  date_created: string;
  document_type: {
    company_document_type_id: DocumentTypeId;
    name: DocumentTypeName;
  };
  extended_data: {
    total_coverage_amount: number;
  };
  file_name: string;
  file_path: string;
  original_file_name: string;
  valid_from: string;
  valid_until: string;
  status_id: DocumentReviewStatus;
};

export interface DocumentPagination {
  has_next: boolean;
  has_previous: boolean;
  page: number;
  size: number;
  total_items: number;
  total_pages: number;
}

export interface DocumentResponse {
  documents: Document[];
  pagination: DocumentPagination;
}

export type AddDocumentRequestBase = {
  company_document_id?: number;
  company_document_type_id: DocumentTypeId;
  file_name: string;
  file_content_type: string;
  file_md5_checksum: string;
  google_recaptcha_token: string;
};

export type AddInsuranceDocumentRequest = AddDocumentRequestBase & {
  valid_until: string;
  valid_from: string;
  extended_data: {
    total_coverage_amount: string;
  };
};

export type AddDriversLicenseDocumentRequest = AddDocumentRequestBase;

export type AddDocumentRequest =
  | AddInsuranceDocumentRequest
  | AddDriversLicenseDocumentRequest;

export type AddDocumentResponse = {
  company_document_id: number;
  s3_presigned_url: string;
  s3_presigned_fields: {
    'CONTENT-MD5': string;
    tagging: string;
    key: string;
    'x-amz-algorithm': string;
    'x-amz-credential': string;
    'x-amz-date': string;
    'x-amz-security-token': string;
    policy: string;
    'x-amz-signature': string;
  };
};
